'use client'
import {useCallback, useEffect, useState, useRef} from 'react'

// Next.js 15 için geliştirilmiş consent management hook'u
type ConsentStatus = 'granted' | 'denied'
type ConsentType = 'GRANTED' | 'DENIED' | null

interface ConsentSettings {
  ad_personalization: ConsentStatus
  ad_storage: ConsentStatus
  ad_user_data: ConsentStatus
  analytics_storage: ConsentStatus
}

interface ConsentManagerReturn {
  consent: ConsentType
  updateConsent: (granted: boolean) => void
  isLoading: boolean
  error: string | null
  clearError: () => void
}

// GTM global tip tanımlaması
declare global {
  interface Window {
    gtag?: (
      command: string,
      action: string,
      parameters: ConsentSettings
    ) => void
  }
}

export const useConsentManager = (): ConsentManagerReturn => {
  const [consent, setConsent] = useState<ConsentType>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const retryCountRef = useRef(0)
  const maxRetries = 3

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const updateGTMConsent = useCallback((consentStatus: ConsentStatus) => {
    try {
      if (typeof window.gtag !== 'function') {
        if (retryCountRef.current < maxRetries) {
          retryCountRef.current++
          setTimeout(() => updateGTMConsent(consentStatus), 1000)
          return
        } else {
          throw new Error('GTM gtag function not available after retries')
        }
      }

      const consentSettings: ConsentSettings = {
        ad_personalization: consentStatus,
        ad_storage: consentStatus,
        ad_user_data: consentStatus,
        analytics_storage: consentStatus,
      }

      window.gtag('consent', 'update', consentSettings)

      if (process.env.APP_ENV === 'development') {
        console.log('GTM Consent updated:', consentSettings)
      }

      retryCountRef.current = 0
      setError(null)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      console.error('GTM consent update failed:', err)
    }
  }, [])

  const updateConsent = useCallback(
    (granted: boolean) => {
      try {
        setIsLoading(true)
        const consentValue: ConsentType = granted ? 'GRANTED' : 'DENIED'
        const consentStatus: ConsentStatus = granted ? 'granted' : 'denied'

        localStorage.setItem('cookieConsent', consentValue)
        setConsent(consentValue)

        updateGTMConsent(consentStatus)

        window.dispatchEvent(
          new CustomEvent('consentUpdated', {
            detail: {consent: consentValue, granted},
          })
        )
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to update consent'
        setError(errorMessage)
        console.error('Consent update failed:', err)
      } finally {
        setIsLoading(false)
      }
    },
    [updateGTMConsent]
  )

  const handleStorageChange = useCallback(
    (e: StorageEvent) => {
      if (e.key === 'cookieConsent') {
        const newConsent = e.newValue as ConsentType
        setConsent(newConsent)

        if (newConsent) {
          const consentStatus: ConsentStatus =
            newConsent === 'GRANTED' ? 'granted' : 'denied'
          updateGTMConsent(consentStatus)
        }
      }
    },
    [updateGTMConsent]
  )

  const handleConsentEvent = useCallback((e: Event) => {
    const customEvent = e as CustomEvent
    if (customEvent.detail?.consent) {
      setConsent(customEvent.detail.consent)
    }
  }, [])

  useEffect(() => {
    try {
      setIsLoading(true)

      const localConsent = localStorage.getItem('cookieConsent') as ConsentType
      setConsent(localConsent)

      if (localConsent) {
        const consentStatus: ConsentStatus =
          localConsent === 'GRANTED' ? 'granted' : 'denied'

        const checkAndUpdateGTM = () => {
          if (typeof window.gtag === 'function') {
            updateGTMConsent(consentStatus)
          } else {
            setTimeout(checkAndUpdateGTM, 100)
          }
        }

        checkAndUpdateGTM()
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to initialize consent'
      setError(errorMessage)
      console.error('Consent initialization failed:', err)
    } finally {
      setIsLoading(false)
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('consentUpdated', handleConsentEvent)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('consentUpdated', handleConsentEvent)
    }
  }, [handleStorageChange, handleConsentEvent, updateGTMConsent])

  return {
    consent,
    updateConsent,
    isLoading,
    error,
    clearError,
  }
}

export default useConsentManager
